import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ShoppingCart,
  Loader2,
  Package,
  DollarSign,
  Eye,
  Mail,
  Store,
  Calendar,
  Truck,
  Heart,
  Clock,
  MapPin,
  User,
  Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DistributorOrders({ distributor }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    if (distributor) {
      fetchOrders();
    }
  }, [distributor, statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/distributor/orders${params}`, getAuthHeaders());
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return <Badge className={styles[status] || "bg-slate-100 text-slate-800"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const totalCommission = orders.reduce((sum, o) => sum + (o.distributor_commission || 0), 0);

  return (
    <div className="space-y-6" data-testid="distributor-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Orders
          </h1>
          <p className="text-slate-500">Orders from your assigned resellers</p>
        </div>

        <div className="flex items-center gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <p className="text-xs text-green-600">Total Commission</p>
            <p className="text-lg font-bold text-green-700">${totalCommission.toFixed(2)}</p>
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No orders found</p>
            <p className="text-sm text-slate-400">Orders from your resellers will appear here</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Reseller</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Total Charged</TableHead>
                <TableHead className="text-right">Your Commission</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.order_id}>
                  <TableCell className="font-mono text-sm">
                    #{order.order_id.slice(-8)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{order.store_name}</p>
                      <p className="text-xs text-slate-500">{order.subdomain}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{order.customer_name}</p>
                    <p className="text-xs text-slate-500">{order.customer_email}</p>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    ${(order.total_charged || order.total || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-medium text-green-600">
                      +${(order.distributor_commission || 0).toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {getStatusBadge(order.status)}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => { setSelectedOrder(order); setShowDetails(true); }}
                      className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Order Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-[2rem]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <Package className="w-6 h-6 text-purple-600" />
              Order Details
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 pt-4">
              <div className="flex justify-between items-start gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Order ID</p>
                  <p className="font-mono font-bold text-slate-900">{selectedOrder.order_id}</p>
                  <div className="mt-2">
                    {getStatusBadge(selectedOrder.status)}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Date</p>
                  <p className="text-sm font-semibold text-slate-900">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Store & Customer Details */}
                <div className="space-y-4">
                  <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 mb-3 text-indigo-600">
                      <Store className="w-4 h-4" />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Store Details</span>
                    </div>
                    <p className="text-base font-bold text-slate-900 mb-1 truncate">{selectedOrder.store_name}</p>
                    <p className="text-sm text-slate-500 truncate">{selectedOrder.subdomain}.edisplayroom.com</p>
                  </div>

                  <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 mb-3 text-indigo-600">
                      <User className="w-4 h-4" />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Customer Details</span>
                    </div>
                    <p className="text-base font-bold text-slate-900 mb-1 truncate">{selectedOrder.customer_name || 'Guest'}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-2 truncate">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{selectedOrder.customer_email}</span>
                    </p>
                    {selectedOrder.contact_email && selectedOrder.contact_email !== selectedOrder.customer_email && (
                      <p className="text-sm text-slate-500 flex items-center gap-2 truncate mt-1">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{selectedOrder.contact_email} (contact)</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Right Column: Delivery Info & Shipping */}
                <div className="space-y-4">
                  {(selectedOrder.deceased_name || selectedOrder.delivery_date || selectedOrder.delivery_time) ? (
                    <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-3 text-indigo-600">
                        <Heart className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Deceased Name</span>
                      </div>
                      {selectedOrder.deceased_name && (
                        <p className="text-base font-bold text-slate-900 mb-1">In Memory of: {selectedOrder.deceased_name}</p>
                      )}
                      {selectedOrder.delivery_date && (
                        <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                          <Calendar className="w-3.5 h-3.5 flex-shrink-0" /> {new Date(selectedOrder.delivery_date).toLocaleDateString()}
                        </p>
                      )}
                      {selectedOrder.delivery_time && (
                        <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                          <Clock className="w-3.5 h-3.5 flex-shrink-0" /> {selectedOrder.delivery_time}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-3 text-indigo-600">
                        <Truck className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Shipping Method</span>
                      </div>
                      {selectedOrder.shipping_method ? (
                        <p className="text-base font-bold text-slate-900 mb-1 break-words leading-tight">{selectedOrder.shipping_method}</p>
                      ) : (
                        <p className="text-sm text-slate-400 italic font-medium">Standard Delivery</p>
                      )}
                    </div>
                  )}

                  {selectedOrder.shipping_address && (
                    <div className="group p-5 bg-slate-50/50 rounded-2xl border border-slate-100/80 transition-all hover:bg-white hover:shadow-md hover:border-indigo-100 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-3 text-indigo-600">
                        <MapPin className="w-4 h-4" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Shipping Address</span>
                      </div>
                      <div className="text-sm text-slate-700 space-y-0.5">
                        {selectedOrder.shipping_address.name && <p className="font-bold text-slate-900">{selectedOrder.shipping_address.name}</p>}
                        {selectedOrder.shipping_address.line1 && <p>{selectedOrder.shipping_address.line1}</p>}
                        {selectedOrder.shipping_address.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                        <p>
                          {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.state, selectedOrder.shipping_address.postal_code].filter(Boolean).join(', ')}
                        </p>
                        {selectedOrder.shipping_address.country && selectedOrder.shipping_address.country !== 'US' && (
                          <p>{selectedOrder.shipping_address.country}</p>
                        )}
                        {selectedOrder.shipping_address.phone && (
                          <p className="flex items-center gap-1.5 mt-1 text-slate-500">
                            <Phone className="w-3.5 h-3.5" /> {selectedOrder.shipping_address.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Order Items</h4>
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-slate-100 flex-shrink-0">
                      <Package className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{item.name}</p>
                      <p className="text-xs text-slate-500">Qty: {item.quantity} × ${item.display_price?.toFixed(2)}</p>

                      {/* Customizations display */}
                      {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                        <div className="mt-2 space-y-1 pl-2 border-l border-slate-200 py-0.5">
                          {Array.isArray(item.customizations) ? (
                            item.customizations.map((cust) => (
                              <div key={cust.id} className="text-[10px]">
                                <div className="flex justify-between font-bold text-slate-600">
                                  <span>{cust.name}</span>
                                  <span className="text-purple-600">+${cust.total.toFixed(2)}</span>
                                </div>
                                {cust.fields.map((f, i) => (
                                  <p key={i} className="text-slate-400 italic leading-tight">
                                    {f.label}: {f.value}
                                  </p>
                                ))}
                              </div>
                            ))
                          ) : (
                            Object.entries(item.customizations).map(([label, val]) => (
                              <p key={label} className="text-[10px] text-slate-400 italic">
                                <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                  ? new Date(val).toLocaleDateString()
                                  : val === true ? 'Yes' : val}
                              </p>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-bold text-slate-900">${(item.display_price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              {/* Breakdown */}
              <div className="bg-purple-50 rounded-2xl p-5 border border-purple-100">
                <h4 className="text-xs font-bold text-purple-800 uppercase tracking-widest mb-4 border-b border-purple-200/50 pb-2">Pricing Breakdown</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-purple-700/70 font-medium">Subtotal</span>
                    <span className="font-bold text-purple-900">${selectedOrder.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-purple-700/70 font-medium">Shipping</span>
                    <span className="font-bold text-purple-900">${(selectedOrder.shipping || selectedOrder.shipping_cost || 0).toFixed(2)}</span>
                  </div>
                  <div className="h-px bg-purple-200/50 my-2"></div>
                  <div className="flex justify-between items-center pb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-slate-900"></div>
                      <span className="text-sm font-bold text-purple-800">Total Charged</span>
                    </div>
                    <span className="text-lg font-black text-purple-900">${(selectedOrder.total_charged || selectedOrder.total || 0).toFixed(2)}</span>
                  </div>

                  <div className="mt-2 pt-4 border-t border-purple-200/50">
                    <div className="flex justify-between items-center bg-white/50 p-3 rounded-xl border border-purple-100">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-sm font-bold text-green-700">Your Commission</span>
                      </div>
                      <span className="text-lg font-black text-green-700">+${(selectedOrder.distributor_commission || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

